import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, User, Building2, Home } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ForgotPasswordModal from "../../components/ForgotPasswordModal";
import "../styles/Login.css";

// MSG91 Widget Configuration (SMS Verification Widget)
const MSG91_WIDGET_ID = "356c7067734f373437333438";
const MSG91_AUTH_TOKEN = "481618TcNAx989nvQ69410832P1"; // Tokenid

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithPhoneOtpWidget } = useAuth();
  useEffect(() => {
    document.body.classList.add("auth-page");

    return () => {
      document.body.classList.remove("auth-page");
    };
  }, []);

  // Get role from URL query parameter, default to "buyer"
  const roleFromUrl = searchParams.get("role");
  const initialUserType = roleFromUrl && ["buyer", "seller", "agent"].includes(roleFromUrl) 
    ? roleFromUrl 
    : "buyer";
  
  // Get returnUrl from query parameter to redirect back after login
  const initialReturnUrl = searchParams.get("returnUrl");

  const [userType, setUserType] = useState(initialUserType);
  const [returnUrl, setReturnUrl] = useState(initialReturnUrl);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [loginMethod, setLoginMethod] = useState("password"); // 'password' | 'otp'
  const [otpState, setOtpState] = useState({
    phone: "",
    backendPhone: "",
    phoneVerificationToken: null,
    phoneVerified: false,
    otpSent: false,
    sending: false,
  });

  const [otpSuccessMsg, setOtpSuccessMsg] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  // Update userType and returnUrl when query parameters change
  useEffect(() => {
    const roleFromUrl = searchParams.get("role");
    const returnUrlFromParams = searchParams.get("returnUrl");
    
    if (roleFromUrl && ["buyer", "seller", "agent"].includes(roleFromUrl)) {
      setUserType(roleFromUrl);
    }
    
    if (returnUrlFromParams) {
      setReturnUrl(returnUrlFromParams);
      // Store in localStorage as backup
      localStorage.setItem("returnUrl", returnUrlFromParams);
      console.log("Return URL detected and stored:", returnUrlFromParams);
    } else {
      // Clear from localStorage if not present
      localStorage.removeItem("returnUrl");
    }
  }, [searchParams]);

  // Validate if the user can login with selected role
  const validateRoleAccess = (registeredUserType, attemptedLoginType) => {
    // Define role access rules
    const roleAccessMap = {
      buyer: ["buyer", "seller"], // Buyer/Tenant can login as buyer or seller
      seller: ["buyer", "seller"], // Seller/Owner can login as buyer or seller
      agent: ["agent"], // Agent/Builder can ONLY login as agent
    };

    const allowedRoles = roleAccessMap[registeredUserType] || [];
    return allowedRoles.includes(attemptedLoginType);
  };

  const getRoleAccessMessage = (registeredType, attemptedType) => {
    const typeLabels = {
      buyer: "Buyer/Tenant",
      seller: "Seller/Owner",
      agent: "Agent/Builder",
    };

    if (registeredType === "agent" && attemptedType !== "agent") {
      return `You are registered as an Agent/Builder. You can only access the Agent/Builder dashboard.`;
    }

    if (registeredType !== "agent" && attemptedType === "agent") {
      return `You are registered as ${typeLabels[registeredType]}. You cannot access the Agent/Builder dashboard. Only registered Agents/Builders can access this section.`;
    }

    return "Access denied for this role.";
  };

  const handleSubmit = async (e, otpOverride = null) => {
    // Prevent default form submission
    if (e) {
      e.preventDefault();
    }
    
    // Prevent double submission
    if (isLoading) {
      return;
    }

    setLoginError("");
    
    setIsLoading(true);

    try {
      let result;
      if (loginMethod === "otp") {
        const otpPhone = otpOverride?.backendPhone || otpState.backendPhone || otpState.phone;
        const otpToken = otpOverride?.phoneVerificationToken || otpState.phoneVerificationToken;
        const otpVerified = otpOverride ? true : otpState.phoneVerified;

        if (!otpPhone) {
          setLoginError("Please enter mobile number");
          setIsLoading(false);
          return;
        }
        if (!otpVerified || !otpToken) {
          setLoginError("Please click Send OTP first to verify your mobile number.");
          setIsLoading(false);
          return;
        }

        result = await loginWithPhoneOtpWidget(
          otpPhone,
          otpToken,
          userType
        );
      } else {
        // Basic validation
        if (!formData.email || !formData.password) {
          setLoginError("Please enter both email and password");
          setIsLoading(false);
          return;
        }
        console.log("Login attempt:", { email: formData.email, userType });
        result = await login(formData.email, formData.password, userType);
      }
      console.log("Login result:", result);

      if (result.success) {
        console.log("Login successful, navigating to dashboard...");
        console.log("Current returnUrl state:", returnUrl);
        console.log("Current returnUrl from searchParams:", searchParams.get("returnUrl"));
        
        // Store current login session
        localStorage.setItem(
          "currentSession",
          JSON.stringify({
            email: loginMethod === "otp"
              ? (otpOverride?.backendPhone || otpState.backendPhone || otpState.phone)
              : formData.email,
            loginType: userType,
            loginTime: new Date().toISOString(),
          })
        );

        // Get returnUrl from state, searchParams, or localStorage (fallback chain)
        const redirectUrl = returnUrl || searchParams.get("returnUrl") || localStorage.getItem("returnUrl");
        
        // Small delay to ensure auth state is fully updated before navigation
        setTimeout(() => {
          // Redirect to returnUrl if provided and valid, otherwise use role-based navigation
          if (redirectUrl) {
            try {
              const decodedUrl = decodeURIComponent(redirectUrl);
              // Validate that it's a relative path (starts with /)
              if (decodedUrl.startsWith('/')) {
                console.log("Redirecting back to property page:", decodedUrl);
                // Clear returnUrl from localStorage after use
                localStorage.removeItem("returnUrl");
                // Use replace: false to allow browser back button
                navigate(decodedUrl, { replace: false });
              } else {
                console.warn("Invalid returnUrl format, using role-based navigation:", decodedUrl);
                // Fallback to role-based navigation
                if (userType === "buyer") {
                  navigate("/buyer-dashboard");
                } else if (userType === "seller") {
                  navigate("/seller-dashboard");
                } else if (userType === "agent") {
                  navigate("/agent-dashboard");
                }
              }
            } catch (error) {
              console.error("Error decoding returnUrl:", error);
              // Fallback to role-based navigation
              if (userType === "buyer") {
                navigate("/buyer-dashboard");
              } else if (userType === "seller") {
                navigate("/seller-dashboard");
              } else if (userType === "agent") {
                navigate("/agent-dashboard");
              }
            }
          } else {
            console.log("No returnUrl found, using role-based navigation");
            // Role-based navigation
            if (userType === "buyer") {
              navigate("/buyer-dashboard");
            } else if (userType === "seller") {
              navigate("/seller-dashboard");
            } else if (userType === "agent") {
              navigate("/agent-dashboard");
            }
          }
        }, 100); // Small delay to ensure auth state is updated
      } else {
        const errorMsg = result.message || "Login failed. Please check your credentials.";
        console.error("Login failed:", errorMsg);
        setLoginError(errorMsg);
      }
    } catch (error) {
      console.error("Login error caught:", error);
      const errorMsg = error.message || error.data?.message || "An error occurred. Please try again.";
      setLoginError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error when user makes changes
    if (loginError) setLoginError("");
  };

  const handleSendOtp = async () => {
    if (otpState.sending || isLoading) return;
    setLoginError("");
    setOtpSuccessMsg("");
    if (!otpState.phone) {
      setLoginError("Please enter mobile number");
      return;
    }

    // Accept only a strict 10-digit Indian mobile number.
    const digits = otpState.phone.replace(/\D/g, "");
    const valid = /^[6-9]\d{9}$/.test(digits);
    const identifier = "91" + digits; // MSG91 widget identifier (no + sign)
    const backendPhone = "+91" + digits; // Backend expects +91XXXXXXXXXX

    if (!valid) {
      setLoginError("Enter a valid Indian mobile number (10 digits, starts from 6-9).");
      return;
    }

    // Check if MSG91 widget is available
    if (!window.initSendOTP) {
      setLoginError("MSG91 widget is not loaded. Please refresh the page and try again.");
      return;
    }

    setOtpState((p) => ({
      ...p,
      sending: true,
      otpSent: false,
      phoneVerified: false,
      phoneVerificationToken: null,
      backendPhone,
    }));

    try {
      const configuration = {
        widgetId: MSG91_WIDGET_ID,
        tokenAuth: MSG91_AUTH_TOKEN,
        identifier,
        success: (data) => {
          // Store verification token
          let verificationToken = null;
          if (typeof data === "string") {
            verificationToken = data;
          } else if (data?.token) {
            verificationToken = data.token;
          } else if (data?.verificationToken) {
            verificationToken = data.verificationToken;
          } else if (data?.data?.token) {
            verificationToken = data.data.token;
          } else {
            verificationToken = JSON.stringify(data);
          }

          setOtpState((p) => ({
            ...p,
            sending: false,
            otpSent: true,
            phoneVerificationToken: verificationToken,
            phoneVerified: true,
            backendPhone,
          }));
          setOtpSuccessMsg("Phone verified successfully!");
          // Auto-login immediately after successful phone verification.
          handleSubmit(null, {
            backendPhone,
            phoneVerificationToken: verificationToken,
          });
        },
        failure: (error) => {
          const errorMessage =
            error?.message || error?.error || error?.toString() || "Phone verification failed. Please try again.";
          setOtpState((p) => ({
            ...p,
            sending: false,
            otpSent: false,
            phoneVerificationToken: null,
            phoneVerified: false,
          }));
          setOtpSuccessMsg("");
          setLoginError(errorMessage);
          alert(errorMessage);
        },
      };

      window.initSendOTP(configuration);
    } catch (error) {
      console.error("Error opening MSG91 widget:", error);
      setOtpState((p) => ({
        ...p,
        sending: false,
        otpSent: false,
        phoneVerificationToken: null,
        phoneVerified: false,
      }));
      setOtpSuccessMsg("");
      setLoginError("Failed to open verification widget. Please try again.");
    }
  };

  const handleUserTypeChange = (type) => {
    setUserType(type);
    // Clear error when changing user type
    if (loginError) setLoginError("");
  };

  // Handle ENTER key to move to next input or submit form
  const handleKeyDown = (e, currentFieldName) => {
    if (e.key === "Enter") {
      e.preventDefault();
      
      // Get all input fields in order
      const inputFields = ["email", "password"];
      const currentIndex = inputFields.indexOf(currentFieldName);
      
      if (currentIndex < inputFields.length - 1) {
        // Move to next input field
        const nextFieldName = inputFields[currentIndex + 1];
        const nextInput = document.querySelector(`input[name="${nextFieldName}"]`);
        if (nextInput) {
          nextInput.focus();
        }
      } else {
        // Last field - submit the form
        handleSubmit(e);
      }
    }
  };

  // Background images mapping for each role
  const backgroundImages = {
    buyer: "/LoginBuy.jpg",
    seller: "/LoginSellerr.jpg",
    agent: "/landingpageagent.jpeg",
  };

  return (
    
    <div className="login-container">
      <div 
        className="background-image"
        style={{
          backgroundImage: `url(${backgroundImages[userType]})`,
        }}
      />
      <div className="login-card" key={userType}>
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Sign in to continue to your account</p>
        </div>

        <div className="user-type-toggle">
          <button
            onClick={() => handleUserTypeChange("buyer")}
            className={userType === "buyer" ? "active" : ""}
          >
            <User size={20} /> Buyer/Tenant
          </button>

          <button
            onClick={() => handleUserTypeChange("seller")}
            className={userType === "seller" ? "active" : ""}
          >
            <Home size={20} /> Seller/Owner
          </button>

          <button
            onClick={() => handleUserTypeChange("agent")}
            className={userType === "agent" ? "active" : ""}
          >
            <Building2 size={20} /> Agent/Builder
          </button>
        </div>

        {/* Role Access Hint */}
        <div className="role-access-hint">
          {userType === "agent" && (
            <p className="hint-text hint-warning">
             
              Only registered Agents/Builders can access this dashboard
            </p>
          )}
          {(userType === "buyer" || userType === "seller") && (
            <p className="hint-text">
             
              Buyers and Sellers can switch between these two dashboards
            </p>
          )}
        </div>

        {/* Error Message */}
        {loginError && (
          <div className="error-message">
            <span className="error-icon">✕</span>
            {loginError}
          </div>
        )}

        <div className="login-method-toggle">
          <button
            type="button"
            onClick={() => {
              setLoginMethod("password");
              setLoginError("");
            }}
            className={`login-method-btn ${loginMethod === "password" ? "active" : ""}`}
          >
            Email & Password
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginMethod("otp");
              setLoginError("");
            }}
            className={`login-method-btn ${loginMethod === "otp" ? "active" : ""}`}
          >
            Mobile OTP
          </button>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {loginMethod === "otp" ? (
            <>
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="text"
                  name="phone"
                  value={otpState.phone}
                  onChange={(e) =>
                    {
                      const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setOtpState((p) => ({
                        ...p,
                        phone: onlyDigits,
                        backendPhone: "",
                        phoneVerificationToken: null,
                        phoneVerified: false,
                        otpSent: false,
                        sending: false,
                      }));
                    }
                  }
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", ".", " "].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  autoComplete="tel"
                  title="Enter 10-digit mobile number"
                  placeholder="please enter your mobile number"
                />
              </div>

              <div className="form-group otp-send-row">
                <button
                  type="button"
                  className="otp-send-btn"
                  onClick={handleSendOtp}
                  disabled={otpState.sending}
                >
                  {otpState.sending ? "Verifying..." : (otpState.phoneVerified ? "Resend OTP" : "Send OTP")}
                </button>
              </div>

              {otpSuccessMsg && (
                <div className="otp-success-message" role="status">
                  {otpSuccessMsg}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onKeyDown={(e) => handleKeyDown(e, "email")}
                  placeholder="john@example.com"
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onKeyDown={(e) => handleKeyDown(e, "password")}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="options-row">
            <label>
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              Remember me
            </label>
            <button 
              type="button"
              onClick={() => setShowForgotPasswordModal(true)}
              className="forgot-password-link"
            >
              Forgot Password?
            </button>
          </div>

          <button 
            type="submit"
            className="login-btn" 
            disabled={isLoading || (loginMethod === "otp" && !otpState.phoneVerified)}
          >
            {isLoading ? "Signing In..." : `Sign In as ${userType === "buyer" ? "Buyer/Tenant" : userType === "seller" ? "Seller/Owner" : "Agent/Builder"}`}
          </button>

          <div className="signup-link">
            Don't have an account?{" "}
            <button type="button" onClick={() => {
              const registerUrl = returnUrl 
                ? `/register?role=${userType}&returnUrl=${encodeURIComponent(returnUrl)}`
                : `/register?role=${userType}`;
              navigate(registerUrl);
            }}>Register now</button>
          </div>
        </form>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
};

export default Login;